import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Editor } from "react-draft-wysiwyg";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import PropTypes from "prop-types";
import ToastDialog from "components/Common/ToastDialog";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Breadcrumbs from "components/Common/BreadcrumbTitle";
import CommonDialog from "components/Common/CommonDialog";
import { Form, FormFeedback } from "reactstrap";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";

import "./main.scss";
import { useFetchNotes } from "hooks/useFetchNotes";
import { useSubmitNote } from "hooks/useSubmitNotes";
import { useDeleteNote } from "hooks/useDeleteNotes";
import { dateFormat, getUser } from "common/utility";
import { useDropzone } from "react-dropzone";
import { downloadSystemConfigFile } from "http/UAM/systemConfig.helper";

const AddNotes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [entityId, setEntityId] = useState();
  const [entityName, setEntityName] = useState("");
  const [anchor, setAnchor] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [product, setProductName] = useState("");
  const [dealId, setDealId] = useState();
  const [showCommonDialog, setShowCommonDialog] = useState({ isShow: false });
  const [showEditTemplate, setShowEditTemplate] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [toastInfo, setToastInfo] = useState({
    message: "",
    showToast: false,
    role: "",
  });

  const { notes, error } = useFetchNotes(entityId, dealId, refetch); // GET Notes Hook
  const { editorState, handleEditorChange, editorError, submitNote } =
    useSubmitNote(0, dealId, setToastInfo, setRefetch); // POST Notes Hook
  // Fetch id and Decode itS
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedId = urlParams.get("id");
    setEntityName(urlParams.get("dealName") || "");
    setAnchor(urlParams.get("anchor") || "");
    setCounterparty(urlParams.get("counterparty") || "");
    if (encodedId) {
      setDealId(atob(encodedId));
    } else {
      dealIdFun();
    }
  }, []);

  const dealIdFun = async () => {
    try {
      const user = decryptedValue(localStorage.getItem("user"));
      const deal = user.dealId;
      setDealId(deal);
    } catch (error) {
      console.error("Error fetching dealId:", error);
    }
  };

  // Date Formatter
  const formatDateTime = (dateTimeString) => {
    const dateTime = new Date(dateTimeString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return dateTime.toLocaleString("en-US", options);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      notes: "",
      fileUpload: [],
    },
    onSubmit: (values) => {
      if (editorState.getCurrentContent().getPlainText() === "" && values.fileUpload.length === 0) {
        setToastInfo({
          message: "Please add a note or upload a file.",
          showToast: true,
          role: "error",
        });
        return;
      }
      if (editorState.getCurrentContent().getPlainText() === "" && values.fileUpload.length > 0) {
        setToastInfo({
          message: "Please add a note first.",
          showToast: true,
          role: "error",
        });
        return;
      }
      const message = `Are you sure you want to ${lastSegment === "edit" ? "update" : "add"
        } note?`;
      setShowCommonDialog({ isShow: true, message });
    },
  });

  const dialogAction = async (action) => {
    if (action === 1) {
      await submitNote(validation.values.fileUpload);
      validation.resetForm();
    }
    setShowCommonDialog({ isShow: false });
  };

  // Backward Navigation
  const navigateBack = () => {
    navigate(-1);
  };

  const pathnameSegments = location.pathname.split("/");
  const lastSegment = pathnameSegments[pathnameSegments.length - 1];
  document.title = `${lastSegment.toUpperCase()} Permission | FundLync`;
  
  
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        validation.setFieldValue("fileUpload", [
          ...validation.values.fileUpload,
          ...acceptedFiles,
        ]);
      }
    },
    [validation.values.fileUpload]
  );


  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  function downloadFile(base64String, ext, fileName) {
    let source = "";
    if (ext == "zip") {
      source = `data:application/zip;base64,${base64String}`;
      const link = document.createElement("a");
      link.href = source;
      link.download = fileName;
      link.click();
    } else {
      source = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64String}`;
      const link = document.createElement("a");
      link.href = source;
      link.download = fileName;
      link.click();
    }
  }
  function formatFunction(file, showToastCallback) {
    const str = file.substring(file.indexOf("com") + 4);
    const filename = file.substring(file.lastIndexOf("/") + 1);
    const ext = str.split(".").pop();
    const obj = {
      fileName: str,
    };
  
    downloadSystemConfigFile(obj)
      .then((response) => {
        if (response.code === 200) {
          downloadFile(response.result, ext, filename);
          showToastCallback({
            message: "Downloaded successfully",
            showToast: true,
            role: "success",
          });
        } else {
          showToastCallback({
            message: response.message,
            showToast: true,
            role: "error",
          });

        }
      })
      .catch((error) => {});
  }
  const handleToast = (info) => {
    setToastInfo(info);
  };
  const extractFileName = (url) => {
    const segments = url.split('/');
    return decodeURIComponent(segments[segments.length - 1]);
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={`${entityName}`} />
          <Row className="vertical-scroll">
            {
              counterparty ? <><p>{`Counterparty: ${counterparty || "NA"} `}</p> <p>{`Anchor: ${anchor || "NA"}`}</p></> : <p>{`Entity: ${anchor || "NA"}`}</p>
            }
            <Col xs="12">
              <Card>
                <CardBody>
                  {showEditTemplate && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <Row>
                      <Col sm={12} className="editorWrapper">
                        <Editor
                          editorState={editorState}
                          onEditorStateChange={handleEditorChange}
                          toolbarHidden={false} // Hide the toolbar
                          wrapperClassName="editorWrapper"
                          editorClassName="editorClass"
                        />
                        {editorError && (
                          <div className="error-text">{editorError}</div>
                        )}
                      </Col>
                    </Row>

                      <Col xl="12">
                        <Form
                          className="needs-validation"
                          onSubmit={(e) => {
                            e.preventDefault();
                            setShowCommonDialog({
                              isShow: true,
                              data: { type: "doc_file" },
                              message: "Are you sure want to submit this Document?",
                            });
                            return false;
                          }}
                        >
                          <div className="row d-flex align-items-center">
                            <Col sm="12"></Col>
                            <Col sm="12" >
                              <div>
                                <div className="d-flex flex-column">
                                  <Label
                                    htmlFor="horizontal-email-Input"
                                    className="col-form-label margin-auto"
                                  >
                                    Upload File (.xlsx, .xls, .jpg, .png, .jpeg, .pdf) *
                                  </Label>
                                  <div
                                    className="drop"
                                    onClick={() =>
                                      validation.setFieldTouched(
                                        "fileUpload",
                                        true
                                      )
                                    }
                                  >
                                    <div {...getRootProps()} className="text-wrapper">
                                      <div className="centered">
                                        <input
                                          type="file"
                                          multiple
                                          name="formatDocument"
                                          id="itrFileExcel"
                                          accept=".jpg, .png, .jpeg, .pdf, .xlsx, .xls"
                                          {...getInputProps()}
                                        />
                                        {/* <label
                                          style={{ marginBottom: 0, fontSize: "14px" }}
                                          htmlFor="itrFileExcel"
                                        > */}
                                          <img
                                            src="https://scfuat.cashinvoice.in/frontend/assets/themes/image/icon/cloud.png"
                                            className="img-fluid"
                                            style={{ width: "30px" }}
                                            alt="Upload"
                                          />
                                          {isDragActive && (
                                            <p className="textLink">Drop here</p>
                                          )}
                                          <span className="textLink">
                                            Browse files to attach
                                          </span>{" "}
                                          or Drop
                                        {/* </label> */}
                                      </div>
                                    </div>
                                  </div>
                                  {validation.values.fileUpload.length > 0 && (
                                    <ul className="mt-2">
                                      {validation.values.fileUpload.map(
                                        (file, index) => (
                                          <li key={index}>
                                            {file.name}
                                            {uploadProgress[file.name] > 0 &&
                                              !errorMessage && (
                                                <div className="progress mt-2">
                                                  <div
                                                    className="progress-bar bg-success"
                                                    role="progressbar"
                                                    style={{
                                                      width: `${uploadProgress[file.name]
                                                        }%`,
                                                    }}
                                                    aria-valuenow="0"
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                  >
                                                    {`${uploadProgress[file.name]}%`}
                                                  </div>
                                                </div>
                                              )}
                                            {errorMessage && (
                                              <div className="alert alert-danger mt-2">
                                                {errorMessage}
                                              </div>
                                            )}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}
                                  {validation.touched.fileUpload &&
                                    validation.errors.fileUpload && (
                                      <FormFeedback
                                        type="invalid"
                                        style={{ display: "block" }}
                                      >
                                        {validation.errors.fileUpload}
                                      </FormFeedback>
                                    )}
                                </div>
                                <hr />
                              </div>
                            </Col>
                            <Col sm="12"></Col>
                          </div>
                        </Form>
                      </Col>
                      <Row>
                        <Col>
                          <div
                            className="text-end"
                            style={{ margin: "20px 0px" }}
                          > <Button
                            type="button"
                            outline
                            color="success"
                            onClick={navigateBack}
                          >
                              Back
                            </Button>
                            <button
                              type="submit"
                              className="btn btn-success save-customer"
                              disabled={loading}
                              style={{ marginLeft: "7px" }}
                            >
                              {loading && (
                                <span
                                  className="spinner-border spinner-border-sm ms-2 me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              )}
                              {loading && <span>Saving</span>}
                              {!loading && <span>Save</span>}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </form>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div>
                    {loading && <div>Loading...</div>}
                    {error && <div>Error: {error}</div>}
                    {!loading &&
                      !error &&
                      (notes.length ? (
                        notes.map((item, index) => (
                         
                          <div className="note-container" key={item.id}>
                            <div className="d-flex justify-content-between note-header">
                              <div className="d-flex align-items-center">
                                <i className="bx bx-file me-1 note-icon"></i>
                                <p className="note-content">{item.notes}</p>
                              </div>


                              <div>
                                <UncontrolledDropdown>
                                  <DropdownToggle tag="a" className="card-drop">
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                 
                                    <DropdownItem
                                      onClick={() =>
                                        useDeleteNote(
                                          item.id,
                                          setRefetch,
                                          setToastInfo
                                        )
                                      }
                                    >
                                      <i
                                        className="bx bx-trash-alt font-size-16 text-success me-1"
                                        id={`deletetooltip-${item.id}`}
                                      ></i>
                                      Delete
                                      <UncontrolledTooltip
                                        placement="top"
                                        target={`deletetooltip-${item.id}`}
                                      >
                                        Delete
                                      </UncontrolledTooltip>
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </div>
                            {item.additionalDocuments[0]?.file_path?.length > 0 &&
                            <div className="">
                            <p>
                            <Label style={{ marginRight: '20px' }}>Documents </Label>
                              {item.additionalDocuments[0].file_path?.map((file, fileIndex) => (
                                <i className="mdi mdi-download"
                                  key={fileIndex}
                                  style={{
                                    fontSize: "25px",
                                    cursor: "pointer",
                                    color: "#34c38f",
                                  }}
                                  onKeyUp={() => { }}
                                  onClick={() => {
                                    formatFunction(
                                      file,
                                      handleToast
                                    );
                                  }}
                                  id={`download-tooltip-${index}-${fileIndex}`}
                                >
                                  <UncontrolledTooltip placement="top" target={`download-tooltip-${index}-${fileIndex}`}>
                                    {extractFileName(file)}
                                  </UncontrolledTooltip>
                                </i>
                              ))}
                            </p>
                          </div>
                          }
                            
                            <div className="d-flex justify-content-between align-items-center mt-3 note-footer">
                              <p className="mb-0">
                                {item.createdByUser.firstName}{" "}
                                {item.createdByUser.lastName}
                              </p>

                              <p className="mb-0">
                                {dateFormat(item.createdAt,"DD-MM-YYYY")} |{" "}
                                {formatDistanceToNow(new Date(item.createdAt), {
                                  addSuffix: true,
                                })}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="mt-3">
                          <h4 className="text-center">No Notes</h4>
                        </div>
                      ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
      {showCommonDialog.isShow && (
        <CommonDialog
          isShow={showCommonDialog.isShow}
          message={showCommonDialog.message}
          postitiveBtn={"Submit"}
          negativeBtn={"Close"}
          onCloseFn={dialogAction}
        />
      )}
    </React.Fragment>
  );
};

AddNotes.propTypes = {
  history: PropTypes.object,
};

export default AddNotes;
